import { useEffect, useState } from 'react';
import {
    EyeIcon, XMarkIcon, CalendarDaysIcon, ShoppingBagIcon,
} from '@heroicons/react/24/outline';
import { useCakeOrders } from '../../hooks/useOrders';
import type { CakeOrderWithOrder } from '../../types';
import { useToast } from '../../hooks/useToast';

// ── Types ─────────────────────────────────────────────────────────────────────

type CakeCollectionStatus = 'pending' | 'collected';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });

function getCakeCollectionStatus(collectedAt: string | null): CakeCollectionStatus {
    return collectedAt ? 'collected' : 'pending';
}

const COLLECTION_STATUS_STYLES: Record<CakeCollectionStatus, string> = {
    pending:   'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
    collected: 'bg-[#f0f7e6] text-[#5a7f28] ring-1 ring-[#c5e08a]',
};

const COLLECTION_STATUS_LABELS: Record<CakeCollectionStatus, string> = {
    pending:   'Pending Collection',
    collected: 'Collected',
};

// ── Sub-components ────────────────────────────────────────────────────────────

function CakeCollectionBadge({ collectedAt }: { collectedAt: string | null }) {
    const status = getCakeCollectionStatus(collectedAt);
    return (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${COLLECTION_STATUS_STYLES[status]}`}>
            {COLLECTION_STATUS_LABELS[status]}
        </span>
    );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function CakeOrdersTab() {
    const { orders, loading, fetchOrders, markCollected } = useCakeOrders();
    const { success, error } = useToast();

    const [activeTab,     setActiveTab]     = useState<CakeCollectionStatus>('pending');
    const [search,        setSearch]        = useState('');
    const [selectedOrder, setSelectedOrder] = useState<CakeOrderWithOrder | null>(null);

    // PIN inline state
    const [showPin,    setShowPin]    = useState(false);
    const [enteredPin, setEnteredPin] = useState('');
    const [pinError,   setPinError]   = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { void fetchOrders(); }, [fetchOrders]);

    const filtered = orders.filter(o => {
        if (getCakeCollectionStatus(o.collected_at) !== activeTab) return false;
        if (search.trim()) {
            const q    = search.toLowerCase();
            const ref  = o.order?.reference?.toLowerCase() ?? '';
            const name = `${o.order?.customer?.first_name ?? ''} ${o.order?.customer?.last_name ?? ''}`.toLowerCase();
            if (!ref.includes(q) && !name.includes(q)) return false;
        }
        return true;
    });

    const pendingCount   = orders.filter(o => !o.collected_at).length;
    const collectedCount = orders.filter(o => !!o.collected_at).length;

    const openDetail = (o: CakeOrderWithOrder) => {
        setSelectedOrder(o);
        setShowPin(false);
        setEnteredPin('');
        setPinError('');
    };

    const handleConfirmCollection = async () => {
        if (!selectedOrder) return;

        // Verify PIN client-side against the value already in memory
        if (enteredPin !== selectedOrder.order?.pin_code) {
            setPinError('Incorrect PIN. Please ask the customer to check their app.');
            return;
        }

        setSubmitting(true);
        try {
            await markCollected(selectedOrder.id);
            success('Cake collection confirmed.');
            setShowPin(false);
            setSelectedOrder(prev => prev ? { ...prev, collected_at: new Date().toISOString() } : prev);
            setActiveTab('collected');
        } catch (err) {
            error(err instanceof Error ? err.message : 'Failed to confirm collection');
            setShowPin(false);
        } finally {
            setSubmitting(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-5">
            {/* Tabs + search */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="inline-flex overflow-hidden rounded-lg border border-stone-300 bg-white shadow-sm">
                    <button
                        type="button"
                        onClick={() => setActiveTab('pending')}
                        className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                            activeTab === 'pending' ? 'bg-stone-900 text-white' : 'text-stone-700 hover:bg-stone-50'
                        }`}
                    >
                        Pending Collection
                        <span className={`inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                            activeTab === 'pending' ? 'bg-white/20 text-white' : 'bg-stone-100 text-stone-600'
                        }`}>
                            {pendingCount}
                        </span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('collected')}
                        className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                            activeTab === 'collected' ? 'bg-stone-900 text-white' : 'text-stone-700 hover:bg-stone-50'
                        }`}
                    >
                        Collected
                        <span className={`inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                            activeTab === 'collected' ? 'bg-white/20 text-white' : 'bg-stone-100 text-stone-600'
                        }`}>
                            {collectedCount}
                        </span>
                    </button>
                </div>
                <input
                    type="text"
                    placeholder="Search by order ref or customer name…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="flex-1 rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[#ee2b74]"
                />
            </div>


            {/* Table */}
            {loading ? (
                <div className="animate-pulse space-y-3 py-4">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="h-10 rounded bg-stone-100" />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <div className="py-12 text-center text-sm text-stone-400">
                    {orders.length === 0 ? 'No cake orders yet.' : 'No cake orders match your filters.'}
                </div>
            ) : (
                <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="border-b border-stone-200 bg-stone-50">
                            <tr>
                                {['Order Ref', 'Customer', 'Size', 'Collection Date', 'Total', 'Collection Status', ''].map(h => (
                                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">{h}</th>
                                ))}
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                            {filtered.map(o => (
                                <tr key={o.id} className="hover:bg-stone-50 transition-colors">
                                    <td className="px-4 py-3 font-medium text-stone-900">
                                        {o.order?.reference ?? o.order_id.substring(0, 8).toUpperCase()}
                                    </td>
                                    <td className="px-4 py-3 text-stone-700">
                                        {o.order?.customer
                                            ? `${o.order.customer.first_name} ${o.order.customer.last_name}`
                                            : '—'}
                                        {o.order?.customer?.cellphone && (
                                            <p className="text-xs text-stone-400">{o.order.customer.cellphone}</p>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-stone-600">{o.selection?.size_label ?? '—'}</td>
                                    <td className="px-4 py-3 text-stone-600">
                                        <div className="flex items-center gap-1.5">
                                            <CalendarDaysIcon className="h-3.5 w-3.5 text-stone-400" />
                                            {formatDate(o.collection_date)}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 font-medium text-stone-900">
                                        R{(o.total_price / 100).toFixed(2)}
                                    </td>
                                    <td className="px-4 py-3">
                                        <CakeCollectionBadge collectedAt={o.collected_at} />
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            onClick={() => openDetail(o)}
                                            className="rounded-md p-1.5 text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors"
                                        >
                                            <EyeIcon className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Detail modal */}
            {selectedOrder && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-[1px]">
                    <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl ring-1 ring-stone-200 overflow-hidden max-h-[90vh] overflow-y-auto">
                        <div className="h-1 w-full bg-[#ee2b74]" />
                        <div className="p-6 space-y-4">
                            {/* Header */}
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-stone-900">Cake Order Detail</h3>
                                    <p className="text-xs text-stone-500 mt-0.5">{selectedOrder.order?.reference}</p>
                                </div>
                                <button
                                    onClick={() => setSelectedOrder(null)}
                                    className="rounded-lg p-1 text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors"
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </button>
                            </div>

                            {/* Collection status banner */}
                            <div className={`rounded-lg px-4 py-3 flex items-center gap-3 ${
                                selectedOrder.collected_at
                                    ? 'bg-[#f0f7e6] border border-[#c5e08a]'
                                    : 'bg-amber-50 border border-amber-200'
                            }`}>
                                <ShoppingBagIcon className={`h-4 w-4 flex-shrink-0 ${selectedOrder.collected_at ? 'text-[#5a7f28]' : 'text-amber-600'}`} />
                                <div>
                                    <p className={`text-sm font-medium ${selectedOrder.collected_at ? 'text-[#5a7f28]' : 'text-amber-700'}`}>
                                        {selectedOrder.collected_at ? 'Collected' : 'Pending Collection'}
                                    </p>
                                    {selectedOrder.collected_at && (
                                        <p className="text-xs text-[#5a7f28]/80 mt-0.5">
                                            {new Date(selectedOrder.collected_at).toLocaleString('en-ZA')}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Customer */}
                            {selectedOrder.order?.customer && (
                                <div className="rounded-lg border border-stone-200 p-4">
                                    <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">Customer</p>
                                    <p className="font-medium text-stone-900">
                                        {selectedOrder.order.customer.first_name} {selectedOrder.order.customer.last_name}
                                    </p>
                                    <p className="text-sm text-stone-500">{selectedOrder.order.customer.cellphone}</p>
                                </div>
                            )}

                            {/* Cake details */}
                            <div className="rounded-lg border border-stone-200 bg-stone-50 p-4">
                                <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">Cake Details</p>
                                <div className="space-y-2">
                                    {selectedOrder.line_items?.map((item, i) => (
                                        <div key={i} className="flex justify-between text-sm">
                                            <span className={i === 0 ? 'font-medium text-stone-900' : 'text-stone-600'}>{item.label}</span>
                                            <span className={i === 0 ? 'font-medium text-stone-900' : 'text-stone-600'}>
                                                {i === 0 ? `R${(item.price / 100).toFixed(2)}` : `+R${(item.price / 100).toFixed(2)}`}
                                            </span>
                                        </div>
                                    ))}
                                    <div className="pt-2 border-t border-stone-200 flex justify-between font-semibold text-stone-900">
                                        <span>Total</span>
                                        <span>R{(selectedOrder.total_price / 100).toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Collection date */}
                            <div className="flex items-center gap-2 text-sm text-stone-600">
                                <CalendarDaysIcon className="h-4 w-4 text-stone-400" />
                                <span>Collection date: <strong>{formatDate(selectedOrder.collection_date)}</strong></span>
                            </div>

                            {/* Cake message */}
                            {selectedOrder.selection?.cake_message && (
                                <div className="rounded-lg border border-stone-200 p-3 text-sm text-stone-600 italic">
                                    🎂 "{String(selectedOrder.selection.cake_message)}"
                                </div>
                            )}

                            {/* Special notes */}
                            {selectedOrder.special_notes && (
                                <div className="rounded-lg border border-stone-200 p-3 text-sm text-stone-600">
                                    <p className="text-xs font-semibold text-stone-400 uppercase mb-1">Special Notes</p>
                                    {selectedOrder.special_notes}
                                </div>
                            )}

                            {/* Complete collection — inline PIN */}
                            {!selectedOrder.collected_at && (
                                <div className="border-t border-stone-100 pt-4 space-y-3">
                                    {!showPin ? (
                                        <button
                                            onClick={() => { setShowPin(true); setEnteredPin(''); setPinError(''); }}
                                            className="w-full rounded-lg bg-[#ee2b74] hover:bg-[#d4245f] text-white text-sm font-semibold py-2.5 transition-colors"
                                        >
                                            Complete Collection
                                        </button>
                                    ) : (
                                        <div className="space-y-3">
                                            <div>
                                                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Verify Collection PIN</p>
                                                <p className="text-xs text-stone-400">Ask the customer for their 4-digit PIN to confirm handover.</p>
                                            </div>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={4}
                                                value={enteredPin}
                                                onChange={e => { setPinError(''); setEnteredPin(e.target.value.replace(/\D/g, '')); }}
                                                placeholder="Enter 4-digit PIN"
                                                autoFocus
                                                className="block w-full rounded-lg border border-stone-300 px-4 py-3 text-center text-lg font-bold tracking-[0.5em] text-stone-900 focus:border-[#ee2b74] focus:ring-2 focus:ring-[#ee2b74] outline-none"
                                            />
                                            {pinError && <p className="text-sm text-red-600 text-center">{pinError}</p>}
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => { setShowPin(false); setPinError(''); setEnteredPin(''); }}
                                                    disabled={submitting}
                                                    className="flex-1 rounded-lg border border-stone-300 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50 transition-colors"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={handleConfirmCollection}
                                                    disabled={submitting || enteredPin.length !== 4}
                                                    className="flex-1 rounded-lg bg-[#ee2b74] hover:bg-[#d4245f] text-white text-sm font-semibold py-2.5 disabled:opacity-50 transition-colors"
                                                >
                                                    {submitting ? 'Confirming…' : 'Confirm'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}